import { Box, Heading, Stack, VStack } from '@chakra-ui/react'
import { getTrackPathPrefix, useContentTrackContext } from '@src/components/sco/Education/ContentTrackContext'
import { ContentTrackDotIndicator } from '@src/components/sco/Education/ContentTrackSectionDetail/ContentTrackDotIndicator'
import { SectionStepProgressBar } from '@src/components/sco/Education/ContentTrackSectionDetail/SectionStepProgressBar'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { ContentTrackSection } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { Fade } from 'react-awesome-reveal'

type SectionStepsListProps = {
  contentTrackSection: ContentTrackSection
  contentTrackSteps: ContentTrackSection['steps']
  cascade?: boolean
  isActive?: boolean
  contentTrackSectionId?: string
  contentTrackStepId?: string
}

export const SectionStepsList = (props: SectionStepsListProps) => {
  const { getBreakpointValue } = useBreakpointContext()

  const trackContext = useContentTrackContext()
  const trackProgress = trackContext.getProgress(props.contentTrackSectionId, props.contentTrackStepId)

  const isCompleted = trackProgress?.completionAsPercent >= 97
  const completionAsPercent = isCompleted ? 100 : trackProgress?.completionAsPercent
  return (
    <VStack align={'stretch'} gap={2} width={'full'}>
      <Fade cascade={props.cascade ? true : false} damping={0.3} duration={1000} triggerOnce={true}>
        {props.contentTrackSteps.map(step => {
          // Check if there are any podcasts, videos, or articles
          const hasContent =
            step.contentAttachments.some(
              attachment => attachment.__typename === 'Article' || attachment.__typename === 'PodcastEpisode'
            ) || step.video

          return (
            <NextLink
              key={step.id}
              passHref={true}
              href={`/${getTrackPathPrefix(props.contentTrackSection.parentContentTrack)}/${
                props.contentTrackSection.parentContentTrack.slug
              }/${props.contentTrackSection.slug}/${step.slug}`}
            >
              <Stack
                align={getBreakpointValue(['flex-start', 'flex-start', 'center'])}
                bgColor={'colorPalette.container/50'}
                borderRadius={'md'}
                direction={'row'}
                gap={4}
                justify={'space-between'}
                padding={4}
                transition={'all 0.2s ease-in'}
                _hover={{
                  bgColor: 'colorPalette.container',
                }}
              >
                <Box>
                  <ContentTrackDotIndicator
                    contentTrackSectionId={props.contentTrackSectionId}
                    contentTrackStepId={step.id}
                  />
                </Box>

                <Stack
                  align={'flex-start'}
                  borderRadius={'md'}
                  direction={getBreakpointValue(['column', 'column', 'row'])}
                  gap={getBreakpointValue([2, 2, 4])}
                  justify={'space-between'}
                  transition={'all 0.2s ease-in'}
                  width={'full'}
                >
                  <Box flex={2}>
                    <Heading as={'span'} size={getBreakpointValue(['sm', 'md'])}>
                      {step.title}
                    </Heading>
                  </Box>
                  {hasContent && (
                    <Box flex={1}>
                      <SectionStepProgressBar
                        contentTrackSection={props.contentTrackSection}
                        contentTrackStep={step}
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>
            </NextLink>
          )
        })}
      </Fade>
    </VStack>
  )
}

SectionStepsList.fragments = {
  ContentTrackSection: () => gql`
    fragment _ on ContentTrackSection {
      id
      slug
      steps {
        id
        slug
        title
        contentAttachments {
          __typename
        }
        ${SectionStepProgressBar.fragments.ContentTrackStep()}
      }
      parentContentTrack {
        id
        slug
        title
        type
        color {
          hex
        }
      }
    }
  `,
}
